/**
 * ChecklistDialog - Shows all onboarding steps with completion status
 */

import { useTutorial } from '@/contexts/TutorialContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
// CORE-BL: the checklist reads the SAME sequence the runtime walks, so a
// clicked index means the same lesson in both.
import { CheckCircle2, Circle, Lock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNavigate } from 'react-router-dom';

interface ChecklistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ChecklistDialog = ({ open, onOpenChange }: ChecklistDialogProps) => {
  const {
    flow,
    currentStep,
    stepsCompleted,
    jumpToStep,
    activeSteps,
    experienceLabel,
  } = useTutorial();
  const navigate = useNavigate();

  if (!flow) return null;

  const steps = activeSteps;

  const getStepStatus = (stepIndex: number, stepId: string) => {
    if (stepsCompleted.includes(stepId)) {
      return 'completed';
    }
    if (stepIndex === currentStep) {
      return 'current';
    }
    if (stepIndex < currentStep) {
      return 'skipped';
    }
    return 'upcoming';
  };

  const handleStepClick = (stepIndex: number, stepPage: string) => {
    // Navigate to the step's page
    navigate(stepPage);
    // Jump to that step
    jumpToStep(stepIndex);
    // Close the dialog
    onOpenChange(false);
  };

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-primary" />;
      case 'current':
        return <Circle className="w-5 h-5 text-primary fill-primary" />;
      case 'skipped':
        return <Circle className="w-5 h-5 text-muted-foreground" />;
      case 'upcoming':
        return <Lock className="w-5 h-5 text-muted-foreground" />;
      default:
        return <Circle className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getStepBadge = (status: string, gateType: 'hard' | 'soft') => {
    if (status === 'completed') {
      return <Badge variant="outline" className="text-xs border-primary text-primary">Done</Badge>;
    }
    if (status === 'current') {
      return <Badge className="text-xs">Active</Badge>;
    }
    if (gateType === 'hard') {
      return <Badge variant="secondary" className="text-xs">Required</Badge>;
    }
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {/* Named from the same authority as everywhere else, rather
                than a ternary that called every current role experience the
                "User Tutorial Checklist". */}
            {experienceLabel ? `${experienceLabel} — checklist` : 'Checklist'}
          </DialogTitle>
          <DialogDescription>
            Track your progress through this walkthrough
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[500px] pr-4">
          <div className="space-y-4">
            {steps.map((step, index) => {
              const status = getStepStatus(index, step.id);
              const icon = getStepIcon(status);
              const badge = getStepBadge(status, step.gateType);

              return (
                <div
                  key={step.id}
                  data-testid={`checklist-item-${step.id}`}
                  onClick={() => handleStepClick(index, step.page)}
                  className={`flex gap-4 p-4 rounded-lg border transition-colors cursor-pointer hover:bg-accent/50 ${
                    status === 'current'
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-card'
                  }`}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-0.5">
                    {icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className="font-medium text-sm">
                        {index + 1}. {step.title}
                      </h4>
                      {badge}
                    </div>
                    
                    <p className="text-sm text-muted-foreground">
                      {step.description}
                    </p>

                    {/* Page hint */}
                    <p className="text-xs text-muted-foreground mt-2">
                      Page: {step.page}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
